<template>
	<AppLayout :title="trans(`menu.events.${eventType}`)">
		<Modal id="create-event-modal">
			<template #title>{{ trans('events.new.title') }}</template>
			<LabeledObject v-if="form.type === EventType.Generic" required>
				<template #label>{{ trans('events.generic.name') }}</template>
				<input v-model="form.name" type="text">
			</LabeledObject>
			<template v-if="form.type === EventType.JoSEII">
				<LabeledObject required>
					<template #label>{{ trans('events.joseii.lemma') }}</template>
					<input v-model="form.lemma" type="text" maxlength="255">
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('events.joseii.semester') }}</template>
					<select v-model="form.semester">
						<option value="spring">{{ trans('events.semester.spring') }}</option>
						<option value="fall">{{ trans('events.semester.fall') }}</option>
					</select>
				</LabeledObject>
			</template>
			<LabeledObject required>
				<template #label>{{ trans('events.hosted.edition') }}</template>
				<input v-model="form.edition" type="text" maxlength="255">
			</LabeledObject>
			<LabeledObject required>
				<template #label>{{ trans('events.hosted.year') }}</template>
				<NumberInput v-model="form.year" :min="0" :max="16535"/>
			</LabeledObject>
			<Switch v-if="form.type === EventType.RCD" v-model="form.online" :label="trans('events.online.online')"/>
			<SelectColleges v-if="![EventType.Generic, EventType.RCD].includes(form.type)" :colleges="form.colleges"/>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template v-if="canCreate" #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-event-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<div class="flex items-end justify-between gap-3">
			<Pagination :resource="events"/>
			<div class="grow"/>
			<Sorting :column="column" :sort-options="sortOptions"/>
		</div>
		<Empty v-if="events.data.length === 0"/>
		<CardGrid v-else>
			<Card v-for="event in events.data" :key="`event-${event.id}`" :title="event.title" :subtitle="event.subtitle" :image="event.image" :image-alt="event.title" has-image class="flex flex-col gap-3">
				<template #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('events.edit', event.id)">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
					<Link v-if="canDelete" :as="SoftButton" :color="Color.Red" :href="route('events.destroy', event.id)" method="delete" replace>
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</Link>
				</template>
			</Card>
		</CardGrid>
		<div class="flex items-end justify-between gap-3">
			<Pagination :resource="events"/>
			<div class="grow"/>
			<Sorting :column="column" :sort-options="sortOptions"/>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, ref, type Ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import {type Event} from '@/Types/Events/Event'
import {EventType} from '@/Types/Enums/EventType'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Pagination from '@/Components/UI/Pagination.vue'
import {type College} from '@/Types/Settings/College'
import Switch from '@/Components/UI/Switches/Switch.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SelectColleges from '@/Components/Search/SelectColleges.vue'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'
import Sorting from '@/Components/UI/Sorting.vue'

const props = defineProps<{
	column:string,
	eventType:string,
	canCreate:boolean,
	canDelete:boolean,
	sortOptions:Array<string>,
	events:PaginatedModel<Event>,
}>()

const working:Ref<boolean> = ref(false)
const events:Ref<PaginatedModel<Event>> = ref(props.events)
const form:Ref<Event> = ref({
	type: props.eventType,
	name: '',
	lemma: '',
	edition: '',
	semester: 'spring',
	year: parseInt(dayjs().format('YYYY')) ?? 0,
	colleges: [] as Array<College>,
	active: false,
	online: false,
} as Event)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		form.value.colleges = form.value.colleges.map((college:College) => college.id)
		axios.post(route('events.store'), form.value).then(() => {
			showSuccess(trans('general.inserted'))
			window.location.reload()
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>